import React, { useState, useEffect } from 'react'
import ReactTooltip from 'react-tooltip'
import withStore from '../../withStore.jsx'
import withFlight from 'containers/withFlight'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { getSelectedParcel } from 'ducks/parcels'
import { isNullOrUndefined } from 'common/util.js'
import { flatten, isEqual, isEmpty } from 'lodash'
import {
  dropdownSections,
  addressFields,
  datasetMatchingFields,
  areaDemographicsFields,
  riskFactorFields,
  ownerFields,
  propertyFields,
  zoningFields,
  structureFields,
  geoFields,
  noShowFields,
} from '../propertySettings.js'
import { PLAN_NAMES } from 'common/settings'
import mapSettings from '../../Map/mapSettings.js'
import ParcelHighlights from '../Components/ParcelHighlights.jsx'
import ParcelDetailsSection from '../Components/ParcelDetailsSection.jsx'
import ParcelInfoCard from '../Components/ParcelInfoCard.jsx'
import ParcelLimitsInfoCard from '../Components/ParcelLimitsInfoCard.jsx'

import { getActiveProjectQuery } from 'ducks/projects'
import { getCurrentQuery } from 'ducks/exportSlice'
import {
  getPlaceParcelPath,
  getWorkingBoundaryPath,
} from 'ducks/boundaries'
import {
  toggleSection,
  fetchLookupLimits,
  getLookupLimits,
  getLookupsRemaining,
  getLookupsTotal,
  isLoadingLookupLimits,
} from 'ducks/userProperties.js'

import { highlightOwnerOnMap } from '../../Insights/insightsUtils'
import { UpsellTooltip } from 'components/Utilities/UpsellTooltip'

/**
 * Renders the details of a parcel.
 *
 * @component
 * @returns {JSX.Element} rendered component
 */
const ParcelDetails = ({ passEventToFlight }) => {
  const isPaidUser = window.data.has_sitecontrol
  const dispatch = useDispatch()
  const currentParcel = useSelector(getSelectedParcel)
  const userLimits = useSelector(getLookupLimits)
  const userLookupsRemaining = useSelector(getLookupsRemaining)
  const userLookupsTotal = useSelector(getLookupsTotal)
  const isLoadingLimits = useSelector(isLoadingLookupLimits)
  // Do not use a useSelector for dropdown prefs,
  // it will cause a re-render that cancels toggle animation #7216
  const store = window.LL.store
  const userDropdowns = store.getState()?.userProperties?.propertySections

  const [hasViewsRemaining, setHasViewsRemaining] = useState(null)
  const [used, setUsed] = useState(null)
  const [highlightedOwner, setHighlightedOwner] = useState(null)
  const [showFilterWarningModal, setShowFilterWarningModal] = useState(false)
  const [pendingFilterValues, setPendingFilterValues] = useState(null)

  const projectQuery = useSelector(getActiveProjectQuery, isEqual)
  const workingBoundaryPath = useSelector(getWorkingBoundaryPath)
  const currentQuery = useSelector(getCurrentQuery)
  const parcelPath = useSelector(state => getPlaceParcelPath(state, workingBoundaryPath))

  const otherProperties = currentParcel?.others || [] // Array of other properties at this location
  const isOthersOpen = userDropdowns['dd-other-properties']
  let isCanada = false
  const level = !window?.data?.current_user_id
  ? PLAN_NAMES.ANON
  : (window?.data?.plan_simple || PLAN_NAMES.FREE)
  const [cards, setCards] = useState([
    { id: 1, text: 'Wondering why our data has different fields in Canada than in the United States?', link: 'https://support.regrid.com/parcel-data/canada-faq' }
  ])
  const preferences = useSelector(s => s.userProperties.parcelPrefs)
  console.log('preferences from parcel details', preferences )


  // Normally the GLMap does this on load, but if there's no GLMap (old ui) then we can do it here
  useEffect(() => {
    if(!userLimits.initialized) {
      dispatch(fetchLookupLimits())
    }
  }, [dispatch, userLimits.initialized])

  useEffect(() => {
    setHasViewsRemaining(userLookupsRemaining > 0)
    setUsed(Math.min(userLimits?.used ?? 0, userLookupsTotal))
  }, [userLimits?.used, userLookupsRemaining, userLookupsTotal])

  useEffect(() => {
    const activeOwner = currentQuery?.parcel?.[parcelPath]?.owner === currentParcel?.fields?.owner

    if(activeOwner) {
      setHighlightedOwner(currentParcel?.fields?.owner)
    } else {
      setHighlightedOwner(null)
    }
  }, [currentQuery, parcelPath, currentParcel])

  if(preferences === null) return null

  /**
   * Handles toggling the dropdown sections.
   *
   * @param {string} id - id of the dropdown section, e.g. 'dd-additional-items'
   */
  const handleSectionToggle = (id) => {
    dispatch(toggleSection(id))
  }

  const handleSendToFilter = (key, value , force = false) => {
    if(key === 'owner') {
      const ownerFilterActive = highlightedOwner === value
      const currentQueryActive = !isEmpty(currentQuery?.parcel?.[parcelPath])

      if(currentQueryActive && !ownerFilterActive && !force) {
        setShowFilterWarningModal(true)
        setPendingFilterValues({ key, value })
        return
      }

      const ownerPath = parcelPath

      const result = highlightOwnerOnMap(value, {
        ownerPath,
        projectQuery,
        highlightedOwner,
        passEventToFlight,
      })

      setHighlightedOwner(result)
    }
  }

  const renderHighlightButton = (labelTag, valueTag, section='') => {
    if(!labelTag || !valueTag) return null

    const tooltipId = isPaidUser
      ? `highlightFilter-${section}`
      : `highlightFilterUpsell-${section}`

    return (
      <div className="owner-filter-container">
        <button
          className={`btn btn-round margin-left-md ${
            highlightedOwner ? 'owner-filter-btn' : 'btn-default'
          }`}
          onClick={() => {
            if(isPaidUser) {
              handleSendToFilter(labelTag, valueTag)
            }
          }}
          data-tip
          data-for={tooltipId}
          disabled={!isPaidUser}
        >
          <i className="fa fa-filter" />
        </button>
        {isPaidUser ? (
          <ReactTooltip
            key={tooltipId}
            id={tooltipId}
            place="top"
            effect="solid"
            delayHide={1000}
            globalEventOff="click"
            className="tt-width-150"
          >
            {highlightedOwner ? 'Filter Active!' : `Highlights owner's other properties on map`}
          </ReactTooltip>
        ) : (
          <UpsellTooltip id={tooltipId} featureMessage="Highlighting owner's other properties on the map" position="right" />
        )}
      </div>
    )
  }

  /**
   * Checks if the dropdown should be rendered based on the parcel object fields.
   *
   * @param {boolean} isOpen - indicates if the dropdown is open
   * @param {string} category - category of the dropdown
   * @param {Object} parcel - parcel object
   * @param {string[]} dropFields - fields to check for in the parcel object
   * @returns {JSX.Element|null} rendered dropdown component or null
   */
  const checkRenderDropdown = (isOpen, category, parcel, dropFields, id) => {
        if(!Array.isArray(dropFields)) {
            return null
    }
    // Check if any of the fields are present in the parcel object
    for(const field of dropFields) {
            const value = parcel.fields[field]
            if(id === 'dd-owner-information' && isCanada ){
        return null
      }
      if(
        !isNullOrUndefined(value) || field === 'all_fields_unchecked' ||
        field.fields?.some(
          (subSecField) =>
            parcel.fields[subSecField] ||
            parcel.fields[subSecField] === 0 ||
            !!parcel.premium_field_metadata[subSecField]
        )
      ) {
        return (
          <ParcelDetailsSection
            key={id}
            id={id}
            isOpen={userDropdowns[id] !== undefined ? userDropdowns[id] : true}
            category={category}
            parcel={parcel}
            fields={dropFields}
            onToggle={handleSectionToggle}
            renderHighlightButton={renderHighlightButton}
          />
        )
      }
    }
    return null
  }

  /**
   * Flattens the array of fields.
   *
   * @param {Array} array - The array of fields.
   * @returns {Array} The flattened array of fields.
   */
  const flattenFields = (array) => {
    const strings = array.filter((field) => typeof field === 'string')
    const objs = array
      .filter((field) => typeof field === 'object')
      .map((field) => {
        // Filter out the fields that are included in the combined arrays
        const fields = field.fields.filter(
          (f) => !field?.combined || !field?.combined.flat().includes(f)
        )
        return fields
      })
    return [...strings, ...flatten(objs)]
  }

  /**
   * Finds the miscellaneous fields that haven't been rendered.
   *
   * @param {Array} allFields - all the category fields
   * @param {string[]} noShowFields - fields marked not to render
   * @param {Object} parcel - parcel object
   * @returns {string[]} miscellaneous fields
   */
  const getMiscFields = (allFields, noShowFields, parcel) => {
    const parcFields = Object.keys(parcel.fields)
    const noShowSet = new Set(noShowFields)
    const flatAllSet = new Set(allFields.flat())
    const { countries } = mapSettings
    const country = parcel.fields['admin0']?.toLowerCase()
    isCanada = country === countries.CA

    const miscFields = parcFields.filter(
      (field) => !noShowSet.has(field) && !flatAllSet.has(field)
    )
    if(preferences.additional_items === false){
      return ['all_fields_unchecked']
      }
        return miscFields
        }

  /** If parcel hasn't loaded, show spinner  */
  if(!currentParcel?.fields || !userLimits || isLoadingLimits) {
    return <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
  }

  // ----- Dropdown Sections -----
  /**
   * Used to calculate misc fields that haven't been rendered,
   * include all category fields but miscFields
   */
  const allFields = [
    ...addressFields,
    ...datasetMatchingFields,
    ...flattenFields(ownerFields),
    ...flattenFields(propertyFields),
    ...flattenFields(zoningFields),
    ...flattenFields(structureFields),
    ...flattenFields(geoFields),
    ...flattenFields(areaDemographicsFields),
    ...flattenFields(riskFactorFields),
  ]

  let miscDropdown = {
    id:     'dd-additional-items',
    title:  'Additional Items',
    fields: [],
    isOpen:
      userDropdowns['dd-additional-items'] !== undefined
        ? userDropdowns['dd-additional-items']
        : true,
  }

  /* Gathers fields we want to render in "Additional Fields" dropdown */
  const miscFields = getMiscFields(allFields, noShowFields, currentParcel)

  if(miscFields?.length > 0) {
      miscDropdown = {
        ...miscDropdown,
        fields: miscFields,
    }
  }

  /**
   * May be more than one address if scity !== city
   * e.g. "Springfield" is a municipality within the "Springfield Township",
   * and both are valid city addresses
   */
  const addressVariants = currentParcel?.formatted_addresses || null

  /**
   * Builds a multi-line address from an array of address components.
   *
   * @param {Array} addressArray - array of address components
   * @returns {JSX.Element|null} multi-line address component or null
   */
  const buildMultiLineAddress = (addressArray) => {
    if(!addressArray || addressArray.length < 4) return null
    const multiLineAddress = (
      <address className="bold margin-none">
        <p className="margin-none">{addressArray[0] + ','}</p>
        <p className="margin-none">{`${addressArray[1]}, ${addressArray[2]} ${addressArray[3]}`}</p>
      </address>
    )
    return multiLineAddress
  }

  const handleClose = (id) => {
    //anon users should always see disclaimer
    if(window.data.signed_in){
      localStorage.setItem('canadaFieldsDisclaimer', true)
    }

    setCards((prevCards) => prevCards.filter((card) => card.id !== id))
  }

  /**
   * Include miscFields in dropdownSections
   */
  const renderedSections = [...dropdownSections, miscDropdown]
  const isDismissed = localStorage.getItem('canadaFieldsDisclaimer')

  const shouldShowLimitsCard = [PLAN_NAMES.ANON, PLAN_NAMES.FREE].includes(level) || ([PLAN_NAMES.PRO, PLAN_NAMES.TEAM].includes(level) && (hasViewsRemaining === false))

  return (
    <>
      {isCanada && !isDismissed &&
        cards.map((card) => (
          <ParcelInfoCard
            key={card.id}
            text={card.text}
            link={card.link}
            onClose={() => handleClose(card.id)}
          />
        ))}
      {isLoadingLimits ? (
        <p>Loading lookup limits...</p>
      ) : ( shouldShowLimitsCard ? (
          <ParcelLimitsInfoCard
            used={String(used)}
            limit={userLimits?.total}
            level={level}
            hasViewsRemaining={hasViewsRemaining}
          />
        ) : null
      )}
      {hasViewsRemaining && (
      <ParcelHighlights
        parcel={currentParcel}
        addressVariants={addressVariants}
        buildMultiLineAddress={buildMultiLineAddress}
        isOpen={userDropdowns['dd-parcel-highlights']}
        isOthersOpen={isOthersOpen}
        otherProperties={otherProperties}
        onToggle={handleSectionToggle}
        renderHighlightButton={renderHighlightButton}

        // renders "Other Records at Location" dropdown if hasOtherProperties
      />)}
      {hasViewsRemaining &&
      renderedSections?.map((dropdown) =>
        checkRenderDropdown(
          userDropdowns[dropdown.id], // isOpen (redux)
          dropdown.title,   // category
          currentParcel,    // parcel
          dropdown.fields,  // dropFields
          dropdown.id       // id
        )
      )}
      {showFilterWarningModal && (
        <div
          className="modal show"
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content rounded-lg" style={{ borderRadius: '10px' }}>
              <div className="modal-header">
              </div>
              <div className="modal-body">
                <p>Showing this owner&apos;s properties on the map will clear your existing filter. Do you want to proceed?</p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-default margin-btm-md"
                  style={{marginBottom: '.5em'}}
                  onClick={() => setShowFilterWarningModal(false)}

                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleSendToFilter(
                      pendingFilterValues.key,
                      pendingFilterValues.value,
                      true
                    )
                    setShowFilterWarningModal(false)
                    setPendingFilterValues(null)
                  }}
                >
                  Yes, Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  )
}

ParcelDetails.propTypes = {
  passEventToFlight:   PropTypes.func.isRequired
}

export default withStore(withFlight(ParcelDetails))
